import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { useTranslation } from "../i18n";
import { Calendar, Camera, Aperture, Clock, MapPin, Image as ImageIcon } from "lucide-react";
import { useDateFormat } from '@/contexts/DateFormatContext';

interface ImageMetadata {
  id: number;
  filename: string;
  fileSize: number;
  mimeType: string | null;
  width: number | null;
  height: number | null;
  uploadedAt: Date;
  // EXIF fields
  cameraMake: string | null;
  cameraModel: string | null;
  lensModel: string | null;
  focalLength: string | null;
  aperture: string | null;
  shutterSpeed: string | null;
  iso: number | null;
  dateTaken: Date | null;
  gpsLatitude: string | null;
  gpsLongitude: string | null;
}

interface ImageMetadataDialogProps {
  image: ImageMetadata | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ImageMetadataDialog({ image, open, onOpenChange }: ImageMetadataDialogProps) {
  if (!image) return null;
  const { t } = useTranslation();

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const { formatDateTime } = useDateFormat();
  const formatDate = (date: Date | null) => {
    if (!date) return "N/A";
    return formatDateTime(date);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Metapodaci slike</DialogTitle>
          <DialogDescription>{image.filename}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* File Info */}
          <div>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              Informacije o datoteci
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Naziv:</span>
                <p className="font-medium">{image.filename}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Veličina:</span>
                <p className="font-medium">{formatFileSize(image.fileSize)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Tip:</span>
                <p className="font-medium">{image.mimeType || "N/A"}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Dimenzije:</span>
                <p className="font-medium">
                  {image.width && image.height ? `${image.width} × ${image.height} px` : "N/A"}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Uploadovano:</span>
                <p className="font-medium">{formatDate(image.uploadedAt)}</p>
              </div>
            </div>
          </div>

          {/* Camera Info */}
          {(image.cameraMake || image.cameraModel) && (
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Camera className="h-4 w-4" />
                Kamera
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {image.cameraMake && (
                  <div>
                    <span className="text-muted-foreground">Proizvođač:</span>
                    <p className="font-medium">{image.cameraMake}</p>
                  </div>
                )}
                {image.cameraModel && (
                  <div>
                    <span className="text-muted-foreground">Model:</span>
                    <p className="font-medium">{image.cameraModel}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Lens Info */}
          {image.lensModel && (
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Aperture className="h-4 w-4" />
                Objektiv
              </h3>
              <div className="text-sm">
                <span className="text-muted-foreground">Model:</span>
                <p className="font-medium">{image.lensModel}</p>
              </div>
            </div>
          )}

          {/* Exposure Settings */}
          {(image.focalLength || image.aperture || image.shutterSpeed || image.iso) && (
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Postavke ekspozicije
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {image.focalLength && (
                  <div>
                    <span className="text-muted-foreground">Žarišna daljina:</span>
                    <p className="font-medium">{image.focalLength}</p>
                  </div>
                )}
                {image.aperture && (
                  <div>
                    <span className="text-muted-foreground">Blenda:</span>
                    <p className="font-medium">{image.aperture}</p>
                  </div>
                )}
                {image.shutterSpeed && (
                  <div>
                    <span className="text-muted-foreground">Brzina zatvarača:</span>
                    <p className="font-medium">{image.shutterSpeed}</p>
                  </div>
                )}
                {image.iso && (
                  <div>
                    <span className="text-muted-foreground">ISO:</span>
                    <p className="font-medium">{image.iso}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Date Taken */}
          {image.dateTaken && (
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Datum snimanja
              </h3>
              <p className="text-sm font-medium">{formatDate(image.dateTaken)}</p>
            </div>
          )}

          {/* GPS Location */}
          {(image.gpsLatitude && image.gpsLongitude) && (
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                GPS lokacija
              </h3>
              <div className="space-y-2">
                <div className="text-sm">
                  <span className="text-muted-foreground">Koordinate:</span>
                  <p className="font-medium">
                    {image.gpsLatitude}, {image.gpsLongitude}
                  </p>
                </div>
                <a
                  href={`https://www.google.com/maps?q=${image.gpsLatitude},${image.gpsLongitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-primary hover:underline"
                >
                  Otvori na Google Maps →
                </a>
              </div>
            </div>
          )}

          {/* No EXIF Data */}
          {!image.cameraMake &&
            !image.cameraModel &&
            !image.lensModel &&
            !image.focalLength &&
            !image.aperture &&
            !image.shutterSpeed &&
            !image.iso &&
            !image.dateTaken &&
            !image.gpsLatitude &&
            !image.gpsLongitude && (
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground">
                  {t.images.noExifData}
                </p>
              </div>
            )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
